import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api'
import { useLocalStorage } from './useLocalStorage'
import { DEFAULT_GROUP_ORDER } from '@/components/tables/columnGroups'

// API response shape
interface UserStateResponse {
  tickers: string[]
  start_date: string | null
  end_date: string | null
  initial_investment: number
  adjust_inflation: boolean
  group_by_underlying: boolean
  visible_columns: string[]
  group_order: string[] | null
  updated_at: string | null
}

// API request shape (snake_case for backend)
interface UserStateUpdateRequest {
  tickers?: string[]
  start_date?: string
  end_date?: string
  initial_investment?: number
  adjust_inflation?: boolean
  group_by_underlying?: boolean
  visible_columns?: string[]
  group_order?: string[]
}

// Local state shape (camelCase for frontend)
interface CalculatorSettings {
  initialInvestment: number
  adjustInflation: boolean
  groupByUnderlying: boolean
}

interface UseUserStateOptions {
  debounceMs?: number
}

const DEFAULT_VISIBLE_COLUMNS = [
  'ticker',
  'underlyingTicker',
  'totalReturn',
  'cagr',
  'dividendYield',
  'dividendsReceived',
]

const DEFAULT_SETTINGS: CalculatorSettings = {
  initialInvestment: 10000,
  adjustInflation: false,
  groupByUnderlying: false,
}

// Default date: 5 years ago
function getDefaultStartDate(): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 5)
  return date.toISOString().split('T')[0]
}

// Today's date
function getTodayDate(): string {
  return new Date().toISOString().split('T')[0]
}

export function useUserState(options: UseUserStateOptions = {}) {
  const { debounceMs = 1000 } = options
  const { user, loading: authLoading } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef<string>('')
  const initialFetchDone = useRef(false)
  const authErrorRef = useRef(false) // Track if we got an auth error to prevent retries
  const pendingChangesRef = useRef<UserStateUpdateRequest>({}) // Accumulate pending changes

  // Local storage as fallback/cache
  const [localTickers, setLocalTickers] = useLocalStorage<string[]>('calculator_tickers', [])
  const [localStartDate, setLocalStartDate] = useLocalStorage<string>(
    'calculator_start_date',
    getDefaultStartDate()
  )
  const [localEndDate, setLocalEndDate] = useLocalStorage<string>(
    'calculator_end_date',
    getTodayDate()
  )
  const [localSettings, setLocalSettings] = useLocalStorage<CalculatorSettings>(
    'calculator_settings',
    DEFAULT_SETTINGS
  )
  const [localVisibleColumns, setLocalVisibleColumns] = useLocalStorage<string[]>(
    'calculator_visible_columns',
    DEFAULT_VISIBLE_COLUMNS
  )
  const [localGroupOrder, setLocalGroupOrder] = useLocalStorage<string[]>(
    'returns-table-group-order',
    DEFAULT_GROUP_ORDER
  )

  // Store setters in refs to avoid dependency issues
  const settersRef = useRef({
    setLocalTickers,
    setLocalStartDate,
    setLocalEndDate,
    setLocalSettings,
    setLocalVisibleColumns,
    setLocalGroupOrder,
  })
  settersRef.current = {
    setLocalTickers,
    setLocalStartDate,
    setLocalEndDate,
    setLocalSettings,
    setLocalVisibleColumns,
    setLocalGroupOrder,
  }

  // Fetch state from server on mount (if authenticated)
  const fetchUserState = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }

    // Double-check we have a token before making the API call
    const token = localStorage.getItem('access_token')
    if (!token) {
      console.warn('No access token found, skipping user state fetch')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await apiClient.get<UserStateResponse>('/user/state')

      // Get setters from ref to avoid stale closures
      const {
        setLocalTickers,
        setLocalStartDate,
        setLocalEndDate,
        setLocalSettings,
        setLocalVisibleColumns,
        setLocalGroupOrder,
      } = settersRef.current

      // Update local storage with server state (only if we have data)
      if (response.tickers && response.tickers.length > 0) {
        setLocalTickers(response.tickers)
      }
      if (response.start_date) {
        setLocalStartDate(response.start_date)
      }
      if (response.end_date) {
        setLocalEndDate(response.end_date)
      }
      // Update all settings at once to avoid multiple re-renders
      if (
        response.initial_investment !== undefined ||
        response.adjust_inflation !== undefined ||
        response.group_by_underlying !== undefined
      ) {
        setLocalSettings((prev) => ({
          ...prev,
          ...(response.initial_investment !== undefined && {
            initialInvestment: response.initial_investment,
          }),
          ...(response.adjust_inflation !== undefined && {
            adjustInflation: response.adjust_inflation,
          }),
          ...(response.group_by_underlying !== undefined && {
            groupByUnderlying: response.group_by_underlying,
          }),
        }))
      }
      if (response.visible_columns && response.visible_columns.length > 0) {
        setLocalVisibleColumns(response.visible_columns)
      }
      if (response.group_order && response.group_order.length > 0) {
        setLocalGroupOrder(response.group_order)
      }

      setError(null)
    } catch (err: unknown) {
      console.error('Failed to fetch user state:', err)
      // Check if it's an auth error (401)
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number } }
        if (axiosError.response?.status === 401) {
          authErrorRef.current = true // Don't retry on auth errors
        }
      }
      // Keep using local storage on error - don't show error to user for GET
    } finally {
      setLoading(false)
      initialFetchDone.current = true
    }
  }, [user]) // Only depend on user, setters are accessed via ref

  // Fetch on auth change
  useEffect(() => {
    if (authLoading) return

    if (!user) {
      setLoading(false)
      initialFetchDone.current = true
      return
    }

    // Only fetch once on initial load, and don't retry after auth errors
    if (!initialFetchDone.current && !authErrorRef.current) {
      fetchUserState()
    }
  }, [user, authLoading, fetchUserState])

  // Debounced save to server - accumulates changes within debounce window
  const saveToServer = useCallback(
    async (state: UserStateUpdateRequest) => {
      if (!user) return

      // Skip if we've already had an auth error (token invalid)
      if (authErrorRef.current) {
        console.warn('Skipping server save due to previous auth error')
        return
      }

      // Check for token
      const token = localStorage.getItem('access_token')
      if (!token) {
        console.warn('No access token, skipping server save')
        return
      }

      // Accumulate this change with any pending changes
      pendingChangesRef.current = { ...pendingChangesRef.current, ...state }

      // Cancel pending save timeout (but keep accumulated changes)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      // Debounce - send all accumulated changes
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          // Get all accumulated changes
          const stateToSave = { ...pendingChangesRef.current }
          // Clear pending changes now that we're about to send them
          pendingChangesRef.current = {}

          const stateJson = JSON.stringify(stateToSave)
          // Skip if identical to last save
          if (stateJson === lastSavedRef.current) return

          await apiClient.put('/user/state', stateToSave)
          lastSavedRef.current = stateJson
          setError(null)
        } catch (err: unknown) {
          console.error('Failed to save user state:', err)
          // Check if it's an auth error (401)
          if (err && typeof err === 'object' && 'response' in err) {
            const axiosError = err as { response?: { status?: number } }
            if (axiosError.response?.status === 401) {
              authErrorRef.current = true // Don't retry on auth errors
              return
            }
          }
          setError('Failed to save to server')
        }
      }, debounceMs)
    },
    [user, debounceMs]
  )

  // Wrapper setters that save to both localStorage and server
  const setTickers = useCallback(
    (value: string[] | ((prev: string[]) => string[])) => {
      setLocalTickers((prev) => {
        const newValue = typeof value === 'function' ? value(prev) : value
        saveToServer({ tickers: newValue })
        return newValue
      })
    },
    [setLocalTickers, saveToServer]
  )

  const setStartDate = useCallback(
    (value: string) => {
      setLocalStartDate(value)
      saveToServer({ start_date: value })
    },
    [setLocalStartDate, saveToServer]
  )

  const setEndDate = useCallback(
    (value: string) => {
      setLocalEndDate(value)
      saveToServer({ end_date: value })
    },
    [setLocalEndDate, saveToServer]
  )

  const setSettings = useCallback(
    (
      value:
        | CalculatorSettings
        | ((prev: CalculatorSettings) => CalculatorSettings)
    ) => {
      setLocalSettings((prev) => {
        const newValue = typeof value === 'function' ? value(prev) : value
        saveToServer({
          initial_investment: newValue.initialInvestment,
          adjust_inflation: newValue.adjustInflation,
          group_by_underlying: newValue.groupByUnderlying,
        })
        return newValue
      })
    },
    [setLocalSettings, saveToServer]
  )

  const setVisibleColumns = useCallback(
    (value: string[] | ((prev: string[]) => string[])) => {
      setLocalVisibleColumns((prev) => {
        const newValue = typeof value === 'function' ? value(prev) : value
        saveToServer({ visible_columns: newValue })
        return newValue
      })
    },
    [setLocalVisibleColumns, saveToServer]
  )

  const setGroupOrder = useCallback(
    (value: string[] | ((prev: string[]) => string[])) => {
      setLocalGroupOrder((prev) => {
        const newValue = typeof value === 'function' ? value(prev) : value
        saveToServer({ group_order: newValue })
        return newValue
      })
    },
    [setLocalGroupOrder, saveToServer]
  )

  return {
    // State
    tickers: localTickers,
    startDate: localStartDate,
    endDate: localEndDate,
    settings: localSettings,
    visibleColumns: localVisibleColumns,
    groupOrder: localGroupOrder,

    // Setters
    setTickers,
    setStartDate,
    setEndDate,
    setSettings,
    setVisibleColumns,
    setGroupOrder,

    // Status
    loading,
    error,
    isAuthenticated: !!user,

    // Actions
    refresh: fetchUserState,
  }
}
